import { Button, Flex, PinInput, PinInputField, Text, useToast } from '@chakra-ui/react';
import { useState } from 'react';

import { usePasswordRecovery } from '../../contexts/PasswordRecoveryContext';

import { confirmForgotPassword } from '@/api';
import { Link } from '@/components/common/Link';
import { FieldWrapper, Form, TextInput } from '@/components/Form';
import { v } from '@/lib/validator';

const resetPasswordSchema = v.object({
  newPassword: v
    .string()
    .matches(/[A-Z]/, 'A senha precisa ter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A senha precisa ter pelo menos uma letra minúscula')
    .matches(/[^A-Za-z0-9]/, 'A senha precisa ter pelo menos um caractere especial')
    .matches(/\d/, 'A senha precisa ter pelo menos um número')
    .min(6, 'A senha precisa ter no mínimo 6 caracteres')
    .required('Campo obrigatório'),
  passwordConfirmation: v
    .string()
    .test('passwords-match', 'As senhas devem ser iguais', function (value) {
      return this.parent.newPassword === value;
    }),
  verificationCode: v
    .string()
    .length(6, 'Código de verificação inválido')
    .required('Campo obrigatório'),
});

export const ResetPasswordStep = () => {
  const { state, dispatch } = usePasswordRecovery();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await confirmForgotPassword({
        body: {
          email: state?.email,
          newPassword: values.newPassword,
          verificationCode: values.verificationCode,
        },
      });
      toast({
        title: 'Senha alterada com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      dispatch({ type: 'NEXT_STEP' });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Erro ao alterar senha',
        description: 'Ocorreu um erro ao alterar a senha, por favor tente novamente',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex direction="column" gap="8" w="full">
      <Flex direction="column" gap="4">
        <Text color="primary.500" fontWeight="bold" fontSize="lg">
          Recuperação de Senha
        </Text>
        <Flex direction="column" w="full">
          <Text>Insira o código recebido no e-mail {state?.email}</Text>
        </Flex>
      </Flex>

      <Form<{ verificationCode: string; newPassword: string; passwordConfirmation: string }>
        initialValues={{
          verificationCode: '',
          newPassword: '',
          passwordConfirmation: '',
        }}
        onSubmit={handleSubmit}
        validationSchema={resetPasswordSchema}
      >
        {(form) => (
          <Flex direction="column" gap="8">
            <Flex direction="column" gap="4">
              <FieldWrapper
                name="verificationCode"
                required
                as={() => {
                  return (
                    <Flex justifyContent="center" gap="2">
                      <PinInput
                        otp
                        onComplete={(value) => {
                          form.setFieldValue('verificationCode', value);
                        }}
                      >
                        <PinInputField borderRadius="xl" />
                        <PinInputField borderRadius="xl" />
                        <PinInputField borderRadius="xl" />
                        <PinInputField borderRadius="xl" />
                        <PinInputField borderRadius="xl" />
                        <PinInputField borderRadius="xl" />
                      </PinInput>
                    </Flex>
                  );
                }}
              />

              <FieldWrapper
                name="newPassword"
                required
                label="Nova Senha"
                as={(props) => <TextInput type="password" {...props} />}
              />
              <FieldWrapper
                name="passwordConfirmation"
                required
                label="Confirmação de senha"
                as={(props) => <TextInput type="password" {...props} />}
              />
            </Flex>

            <Flex gap="2" w="full" alignSelf="center">
              <Button type="button" as={Link} to="/login" w="100%">
                Cancelar
              </Button>
              <Button isLoading={loading} type="submit" variant="@primary" w="100%">
                Salvar
              </Button>
            </Flex>
          </Flex>
        )}
      </Form>
    </Flex>
  );
};
